import { CONTAINER } from '@/platform/config/inversify.config';
import { TYPES } from '@/platform/config/inversify.types.config';

import { ModuleConfig } from '@/platform/models/config/app-config';
import { Dictionary } from '@/platform/models/dictionary';
import { EnvironmentEnum } from '@/platform/models/environment/environment.enum';
import { Filter } from '@/platform/models/filters/filter';
import { FilterConfig, RegularFilterConfig } from '@/platform/models/filters/filter-config';
import {
  DateFilterSelectedValue,
  FilterSelectedValue,
  RealtimeFilterSelectedValue,
  RegularFilterSelectedValue
} from '@/platform/models/filters/filter-selected-values';
import { FilterModel } from '@/platform/models/filters/site-filter-config';

import { FiltersSelectedValuesService } from '@/platform/services/filters-selected-values.service';

import { AUTH_GETTER_TYPES } from '@/platform/store/auth/auth.getter.types';
import { FILTERS_GETTER_TYPES } from '@/platform/store/filters/filters.getter.types';
import { PLATFORM_GETTER_TYPES } from '@/platform/store/platform/platform.getter.types';
import { STORE } from '@/platform/store/store';

import StatsClient from '@empathybroker/tsclient-stats';
import { StatsEnvironments } from '@empathybroker/tsclient-stats/dist/enums/stats-environments';
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum';
import { StatsParams } from '@empathybroker/tsclient-stats/dist/models/params/stats-params';
import { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response';

export class StatsService {

  public static getStatsReport(reportId: StatsReportsIdsEnum, reportParams = {}, requestCompareData = false,
    timeoutRequest?: number): Promise<any> {
    if (!STORE.getters[FILTERS_GETTER_TYPES.areValid]) {
      return;
    }

    if (requestCompareData) {
      const filtersCompareIds = STORE.getters[FILTERS_GETTER_TYPES.compareIdsActive];
      return new Promise((resolve, reject) => {
        Promise.all(
          filtersCompareIds.map(
            (filtersCompareId: string) => this.buildStatsRequest(reportId, reportParams, timeoutRequest, filtersCompareId))
        )
          .then((value: any[]) => {
            const response: Dictionary<StatsResponse> = {};
            filtersCompareIds.forEach((filtersCompareId: string, index: number) => {
              response[filtersCompareId] = value[index];
            });
            resolve(response);
          })
          .catch(reject);
      });
    }

    return this.buildStatsRequest(reportId, reportParams, timeoutRequest);
  }

  private static buildStatsRequest(reportId: StatsReportsIdsEnum, reportParams: Partial<StatsParams>, timeoutRequest: number,
    filtersCompareId = FiltersSelectedValuesService.MAIN): Promise<StatsResponse> {
    const statsClientService: StatsClient = CONTAINER.get<(siteId: string, environment: StatsEnvironments) => StatsClient>(
      TYPES.StatsClientService)(STORE.getters[PLATFORM_GETTER_TYPES.site].id, StatsService.getStatsEnvironmentFromState());

    const statsParams = this.mergeReportParamsWithState(reportParams, filtersCompareId);

    return new Promise((resolve, reject) => {
      statsClientService.getStatsReport(STORE.getters[AUTH_GETTER_TYPES.token], reportId, statsParams, timeoutRequest)
        .then((response) => resolve(response))
        .catch((error) => {
          if (error.status === 403) {
            const routerService = (CONTAINER.get(TYPES.RouterService) as any);
            const CONFIG: Dictionary<ModuleConfig> = CONTAINER.get(TYPES.ModulesConfig);
            routerService.replace(CONFIG.platform.routes.logout);
          } else {
            reject(error);
          }
        });
    });
  }

  private static getStatsEnvironmentFromState(): StatsEnvironments {
    switch (STORE.getters[PLATFORM_GETTER_TYPES.environment]) {
      case EnvironmentEnum.LIVE:
        return StatsEnvironments.LIVE;
      case EnvironmentEnum.STAGING:
      default:
        return StatsEnvironments.STAGING;
    }
  }

  private static mergeReportParamsWithState(reportParams: Partial<StatsParams>, filtersCompareId: string): StatsParams {
    let statsParams: StatsParams = this.getFiltersParamsFromState(filtersCompareId);
    statsParams = {
      ...statsParams,
      ...reportParams,
      filters: {
        ...statsParams.filters,
        ...reportParams.filters
      }
    };

    return statsParams;
  }

  private static getFiltersParamsFromState(filtersCompareId: string): StatsParams {
    let filtersParams: StatsParams = {
      startDate: undefined,
      endDate: undefined,
      filters: {}
    };
    Object.values(STORE.getters[FILTERS_GETTER_TYPES.enabledFilters]).forEach((filter: Filter) => {
      let filterSelectedValue: FilterSelectedValue;
      if (filter.config.compareEnabled) {
        filterSelectedValue = filter.selectedValues[filtersCompareId];
      } else {
        filterSelectedValue = filter.selectedValues[FiltersSelectedValuesService.MAIN];
      }
      filtersParams = {
        [FilterModel.REGULAR]: this.fillFiltersParamsWithRegularFilterSelectedValues,
        [FilterModel.DATE]: this.fillFiltersParamsWithDateFilterSelectedValues,
        [FilterModel.REAL_TIME]: this.fillFiltersParamsWithRealtimeFilterSelectedValues
      }[filter.config.modelName](filtersParams, filterSelectedValue, filter.config);
    });
    return filtersParams;
  }

  private static fillFiltersParamsWithRegularFilterSelectedValues(filtersParams: StatsParams,
    filterSelectedValue: FilterSelectedValue, filterConfig: FilterConfig): StatsParams {
    filterSelectedValue = (filterSelectedValue as RegularFilterSelectedValue);
    const regularFilterConfig = (filterConfig as RegularFilterConfig);
    filtersParams.filters[(regularFilterConfig.statsId || regularFilterConfig.id)] = filterSelectedValue.values.map((value: string) => {
      const valueConfig = regularFilterConfig.values[value];
      return (valueConfig.statsId || valueConfig.id);
    });
    return filtersParams;
  }

  private static fillFiltersParamsWithDateFilterSelectedValues(filtersParams: StatsParams,
    filterSelectedValue: FilterSelectedValue, filterConfig: FilterConfig): StatsParams {
    filterSelectedValue = (filterSelectedValue as DateFilterSelectedValue);
    if (!!filterSelectedValue.startDateInMillis && !!filterSelectedValue.endDateInMillis) {
      filtersParams.startDate = new Date(filterSelectedValue.startDateInMillis);
      filtersParams.endDate = new Date(filterSelectedValue.endDateInMillis);
    }
    return filtersParams;
  }

  private static fillFiltersParamsWithRealtimeFilterSelectedValues(filtersParams: StatsParams,
    filterSelectedValue: FilterSelectedValue, filterConfig: FilterConfig): StatsParams {
    filterSelectedValue = (filterSelectedValue as RealtimeFilterSelectedValue);
    if (!!filterSelectedValue.intervalInMinutes) {
      filtersParams.endDate = new Date(Date.now());
      filtersParams.startDate = new Date(filtersParams.endDate.getTime() - ((filterSelectedValue.intervalInMinutes * 60) * 1000));
    }
    return filtersParams;
  }

}



// WEBPACK FOOTER //
// ./src/platform/services/stats.service.ts